import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth debe usarse dentro de AuthProvider');
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [token, setToken] = useState(localStorage.getItem('authToken'));
	const [loading, setLoading] = useState(true);

	// Configurar axios para incluir token en todas las peticiones
	useEffect(() => {
		if (token) {
			axios.defaults.headers.common['Authorization'] = `Token ${token}`;
			localStorage.setItem('authToken', token);
		} else {
			delete axios.defaults.headers.common['Authorization'];
			localStorage.removeItem('authToken');
		}
	}, [token]);

	// Cargar usuario al iniciar
	useEffect(() => {
		if (token) {
			loadUser();
		} else {
			setLoading(false);
		}
	}, [token]);

	const loadUser = async () => {
		try {
			const response = await axios.get(`${API_URL}/api/auth/me/`);
			setUser(response.data);
			return response.data;
		} catch (error) {
			// Solo mostrar error si no es 401 (no autorizado), que es esperado cuando no hay token
			if (error.response?.status !== 401) {
				console.error('Error cargando usuario:', error);
			}
			// Si hay error, limpiar token
			setToken(null);
			setUser(null);
		} finally {
			setLoading(false);
		}
	};

	const login = async (username, password) => {
		try {
			const response = await axios.post(`${API_URL}/api/auth/login/`, {
				username,
				password
			});
			
			const { token: newToken, user: userData, requiere_cambio_password } = response.data;
			// Setear el header de axios inmediatamente para que las peticiones
			// subsiguientes (ej. carga de empresas tras redirect) ya tengan el token
			axios.defaults.headers.common['Authorization'] = `Token ${newToken}`;
			localStorage.setItem('authToken', newToken);
			setToken(newToken);
			setUser(userData);
			return {
				success: true,
				requiere_cambio_password: requiere_cambio_password || false,
				role: userData?.profile?.role || null,
			};
		} catch (error) {
			console.error('Error completo de login:', error);
			console.error('Response data:', error.response?.data);
			console.error('Response status:', error.response?.status);
			
			let message = 'Error al iniciar sesión';
			
			if (!error.response) {
				// Error de red o servidor no disponible
				message = 'No se pudo conectar al servidor. Verifica que el backend esté corriendo.';
			} else if (error.response.status === 400) {
				// Error de validación
				message = error.response.data?.non_field_errors?.[0] || 
				         error.response.data?.detail || 
				         error.response.data?.username?.[0] ||
				         error.response.data?.password?.[0] ||
				         'Credenciales inválidas. Verifica usuario y contraseña.';
			} else if (error.response.status === 401) {
				message = 'Credenciales inválidas.';
			} else if (error.response.status >= 500) {
				message = 'Error del servidor. Intenta más tarde.';
			} else {
				message = error.response.data?.detail || 
				         error.response.data?.message ||
				         'Error al iniciar sesión';
			}
			
			return { success: false, error: message };
		}
	};

	const register = async (userData) => {
		try {
			const response = await axios.post(`${API_URL}/api/auth/register/`, userData);
			
			const { token: newToken, user: userDataResponse } = response.data;
			setToken(newToken);
			setUser(userDataResponse);
			return { success: true };
		} catch (error) {
			const errors = error.response?.data || {};
			return { success: false, errors };
		}
	};

	const logout = async () => {
		try {
			await axios.post(`${API_URL}/api/auth/logout/`);
		} catch (error) {
			console.error('Error en logout:', error);
		} finally {
			setToken(null);
			setUser(null);
		}
	};

	const isAdmin = () => {
		return user?.profile?.role === 'admin' || user?.is_superuser;
	};

	const isSuperuser = () => user?.is_superuser === true;

	const isGestor = () => {
		return user?.profile?.role === 'gestor' || isAdmin();
	};

	const isContador = () => {
		return user?.profile?.role === 'contador';
	};

	/** Admin O Contador: ambos pueden gestionar usuarios y empresas (con alcance diferente) */
	const isAdminOrContador = () => {
		return isAdmin() || isContador();
	};
	
	const isCliente = () => {
		return user?.profile?.role === 'cliente';
	};

	const value = {
		user,
		token,
		loading,
		login,
		register,
		logout,
		isAdmin,
		isSuperuser,
		isGestor,
		isContador,
		isAdminOrContador,
		isCliente,
		loadUser
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

